import { Button, Column, Loader, Modal, ModalFooter, Row, Tab, TabGroup, Text } from '@amzn/meridian';
import { ErrorAlert } from 'components/ErrorAlert';
import { useGlobalToasterContext } from 'components/GlobalToaster/GlobalToasterContext';
import {
  type ProjectPermissionsQuery,
  type TpShareModalLdapPermissionFragment,
  type TpShareModalUserPermissionFragment,
  useProjectPermissionsQuery,
  useUpdateTrafficProjectPermissionsMutation,
} from 'generated/graphql';
import { useUserData } from 'hooks/use-user-data';
import { type FC, useCallback, useEffect, useState } from 'react';
import { LDAPPermissionsTable } from './LDAPPermissionsTable/LDAPPermissionsTable';
import { deepOmitTypename } from './TPShareModalUtil';
import { UsersPermissionTable } from './UsersPermissionsTable/UsersPermissionTable';

export type TPShareModalProps = {
  open: boolean;
  skipQuery: boolean;
  onClose: () => void;
  projectId: string;
};

export const TPShareModalTabsMap = {
  ldap: 'ldap-tab',
  users: 'users-tab',
} as const;

export type TPShareModalTab = (typeof TPShareModalTabsMap)[keyof typeof TPShareModalTabsMap];

export const TPShareModal: FC<TPShareModalProps> = ({ open, onClose, projectId, skipQuery }) => {
  const [currentTab, setCurrentTab] = useState<TPShareModalTab>('users-tab');
  const [userPermissionsSet, setUserPermissionsSet] = useState<TpShareModalUserPermissionFragment[]>([]);
  const [ldapPermissionsSet, setLdapPermissionsSet] = useState<TpShareModalLdapPermissionFragment[]>([]);

  const { alias } = useUserData();
  const { onOpenToast } = useGlobalToasterContext();

  // We use the project permissions query here instead of passing them as props because firing the query at
  // the page level causes refetches dues to issues with other graph objects causing cache busting.
  const { data, loading, error } = useProjectPermissionsQuery({
    onCompleted(data) {
      initCurrentPermissionsState(data);
    },
    skip: skipQuery,
    variables: {
      trafficProjectId: projectId,
    },
  });

  const [updateProjectPermissions, { loading: projectPermissionsUpdateLoading, error: projectPermissionsUpdateError }] =
    useUpdateTrafficProjectPermissionsMutation({
      errorPolicy: 'all',
      onCompleted(data) {
        if (data?.shareTrafficProject) {
          onOpenToast({
            alertType: 'success',
            timeout: 2500,
            toastMessage: 'Permissions updated',
          });
          closeModal();
        }
      },
    });

  const initCurrentPermissionsState = useCallback((data?: ProjectPermissionsQuery) => {
    if (!data?.trafficProject.permissions) return;

    const newUserPermissions: TpShareModalUserPermissionFragment[] = [];
    const newLdapPermissions: TpShareModalLdapPermissionFragment[] = [];
    data.trafficProject.permissions.forEach((permissionObj) => {
      if (permissionObj.__typename === 'TrafficProjectUserPermission') newUserPermissions.push(permissionObj);
      if (permissionObj.__typename === 'TrafficProjectLdapPermission') newLdapPermissions.push(permissionObj);
    });
    setUserPermissionsSet(newUserPermissions);
    setLdapPermissionsSet(newLdapPermissions);
  }, []);

  const closeModal = useCallback(() => {
    setUserPermissionsSet([]);
    setLdapPermissionsSet([]);
    onClose();
  }, [
    onClose,
  ]);

  useEffect(() => {
    // If the modal is open, initialize the current permissions state.
    // We do this to reset the modal on re-opening.
    if (open) {
      initCurrentPermissionsState(data);
    }
  }, [
    data,
    initCurrentPermissionsState,
    open,
  ]);

  return (
    <Modal aria-describedby="project-share-modal" open={open} scrollContainer="viewport" title="Share Project">
      <Column
        height="70vh"
        heights={[
          'fit',
          'fit',
          'fill',
        ]}
      >
        <Column>
          {!loading && error ? <ErrorAlert contextMessage="Unable to load project permissions" error={error} /> : null}
          {!projectPermissionsUpdateLoading && projectPermissionsUpdateError ? (
            <ErrorAlert contextMessage="Failed to update project permissions" error={projectPermissionsUpdateError} />
          ) : null}
          <Text>Share this project with specific people and LDAP groups. Sharing will give users edit privileges.</Text>
        </Column>
        <Row>
          <TabGroup aria-labelledby="tablist-heading" onChange={setCurrentTab} value={currentTab}>
            <Tab aria-controls="user-share-settings" disabled={loading} id={TPShareModalTabsMap.users} value={TPShareModalTabsMap.users}>
              Users
              {userPermissionsSet.length > 0 ? ` (${userPermissionsSet.length})` : null}
            </Tab>
            <Tab aria-controls="ldap-share-settings" disabled={loading} id={TPShareModalTabsMap.ldap} value={TPShareModalTabsMap.ldap}>
              LDAP Groups
              {ldapPermissionsSet.length > 0 ? ` (${ldapPermissionsSet.length})` : null}
            </Tab>
          </TabGroup>
        </Row>
        {loading || projectPermissionsUpdateLoading ? (
          <Row alignmentHorizontal="center">
            <Loader data-cy="tp-share-modal-loader" />
          </Row>
        ) : (
          <Row alignmentVertical="top">
            {currentTab === TPShareModalTabsMap.users && data && alias ? (
              <UsersPermissionTable
                projectData={data.trafficProject}
                setUserPermissions={setUserPermissionsSet}
                userData={{
                  alias,
                }}
                userPermissions={userPermissionsSet}
              />
            ) : null}

            {currentTab === TPShareModalTabsMap.ldap ? (
              <LDAPPermissionsTable selectedGroups={ldapPermissionsSet} setSelectedGroups={setLdapPermissionsSet} />
            ) : null}
          </Row>
        )}
      </Column>
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          <Button onClick={closeModal} type="secondary">
            Cancel
          </Button>
          <Button
            disabled={loading || error !== undefined || !data}
            onClick={() =>
              updateProjectPermissions({
                variables: {
                  groups: ldapPermissionsSet.map((obj) => obj.ldapGroup),
                  people: userPermissionsSet.map((obj) => deepOmitTypename(obj.user)),
                  shareTrafficProjectId: data!.trafficProject.id,
                },
              })
            }
            type="primary"
          >
            Confirm
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};
